import {SidebarTrigger} from "@/components/ui/sidebar.tsx";
import {StatusBar, Style} from "@capacitor/status-bar";
import {Badge, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger} from "@heroui/react";
import {Icon} from "@iconify/react";
import {useQueryClient} from "@tanstack/react-query";

import {Link, useMatch, useNavigate} from "@tanstack/react-router";
import {useWindowSize} from "@uidotdev/usehooks";
import React, {useEffect} from "react";
import logo from "./assets/logo.png";

import PButton from "./components/PButton.tsx";
import {UserRole} from "./constants.ts";
import {useGlobalModal} from "./core/GlobalModal.tsx";
import {useJwtAuth} from "./JWTAuthProvider.tsx";
import ContactSupportModal from "./user/ContactSupportModal.tsx";
import queryKeys from "./utilities/queryKeys.ts";

const Header = ({isPublicPage = false, showBackUrl = true, backUrl = null}: { isPublicPage?: boolean, showBackUrl?: boolean, backUrl?: string | null }) => {
    const jwt = useJwtAuth();
    const user = jwt.user;
    const routeMatch = useMatch({strict: false});

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const globalModal = useGlobalModal();

    const size = useWindowSize();
    const isMobile = (size.width && size.width <= 767); // matches useSidebar.isMobile behaviour

    useEffect(() => {
        if (localStorage.theme === 'light') {
            document.querySelector('html')?.classList.remove('dark');
        }
    }, []);

    const doSignOut = async () => {
        try {
            await jwt.signOut();
            navigate({to: '/login/'});
        } catch (reason) {
            alert(`Error: ${reason}`);
        }
    };

    const toggleDark = async () => {
        try {
            localStorage.theme = (localStorage.theme === 'light' ? 'dark' : 'light');
            document.querySelector('html')?.classList.toggle('dark', localStorage.theme === 'dark');
            document.querySelector('html')?.classList.toggle('light', localStorage.theme === 'light');

            StatusBar.setStyle({style: (localStorage.theme === 'light' ? Style.Light : Style.Dark)});
        } catch (reason) {
            alert(`Error: ${reason}`);
        }
    };

    if (!isMobile && !isPublicPage && !!user) {
        return <div className={"mb-4"}></div>
    }

    return (
        <header className="max-w-6xl w-11/12 mx-auto bg-white dark:bg-[#252525] rounded-full mt-2 p-3 px-4 mb-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
                {!isPublicPage && !!user && <SidebarTrigger id={"HeaderSidebarTrigger"} />}

                {!isMobile && showBackUrl && (
                    <Link to={routeMatch.staticData?.backUrl || backUrl || `/profiles/me`}><Icon icon="material-symbols-light:arrow-back" className="text-2xl" /></Link>
                )}
                <a className="inline" href="#" onClick={(e) => {
                    e.preventDefault();
                    navigate({to: user ? `/profiles/me/` : '/login/'});
                }}>
                    <img className="ThemeDark" style={{maxHeight: '2rem'}} src={logo} alt="logo" />
                </a>
            </div>
            {(!isMobile || isPublicPage) && <div>
                {user && user.role !== UserRole.Master && <>
                    <Badge color="primary" size={"sm"} shape="circle"
                           content={((user?.unread_messages_count || 0) > 9 ? '9+' : (user?.unread_messages_count || 0))}
                           isInvisible={!user?.unread_messages_count}
                    >
                        <PButton isIconOnly variant={"light"} onPress={() => {
                            navigate({to: '/messages/'});
                            queryClient.invalidateQueries({queryKey: queryKeys.conversations()})
                        }}><Icon icon={"material-symbols-light:chat-outline-rounded"} className={"text-2xl"} /></PButton>
                    </Badge>
                </>
                }

                {isMobile
                    ? <Dropdown placement="bottom-end">
                        <DropdownTrigger>
                            <PButton variant="light" isIconOnly={true}>
                                <Icon icon="material-symbols-light:menu" className="text-2xl" />
                            </PButton>
                        </DropdownTrigger>
                        <DropdownMenu
                            disallowEmptySelection
                            aria-label="Header Menu"
                            selectionMode="single"
                            onSelectionChange={(e) => {
                                switch (e.currentKey) {
                                    case 'support':
                                        globalModal.open(<ContactSupportModal />);
                                        break;
                                    case 'dark':
                                        toggleDark();
                                        break;
                                    case 'signout':
                                        doSignOut();
                                        break;
                                }
                            }}
                        >
                            <DropdownItem key="support"
                                          startContent={<Icon icon={"material-symbols-light:help-outline"} className={"text-2xl inline"} />}>
                                Contact Support
                            </DropdownItem>
                            <DropdownItem key="dark" showDivider
                                          startContent={<Icon icon={"material-symbols-light:dark-mode-outline"} className={"text-2xl inline"} />}>
                                Dark Mode
                            </DropdownItem>
                            <DropdownItem key="signout"
                                          startContent={<Icon icon="material-symbols-light:output-circle" className={"text-primary text-2xl -rotate-90 inline"} />}>
                                Sign Out
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                    : <>
                        <PButton isIconOnly variant={"light"} className="" onPress={() => globalModal.open(<ContactSupportModal />)}><Icon icon={"material-symbols-light:help-outline"} className={"text-2xl"} /></PButton>

                        <PButton isIconOnly variant={"light"} onPress={toggleDark} className=""><Icon icon={"material-symbols-light:dark-mode-outline"} className={"text-2xl"} /></PButton>
                        {user && (
                            <span title={`Signed in as ${user?.email}`}>
                        <PButton isIconOnly onPress={doSignOut} className="border border-primary light:bg-transparent ml-2">
                            <Icon icon="material-symbols-light:output-circle" className={"text-primary text-2xl -rotate-90"} />
                        </PButton>
                    </span>
                        )}
                    </>
                }
            </div>}
        </header>
    );
}

export default Header;
